import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { Button, Collapse, CollapseProps, Divider, Drawer } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { FlexWrapper } from 'statics/styles/StyledComponents';
import styled from 'styled-components';
import { useResponsive } from 'utilities/hooks';

const { Panel } = Collapse;

type Props = {
  open: boolean;
  onClose: Function;
  title: string;
  placement?: 'left' | 'right' | 'top' | 'bottom';
  children?: React.ReactNode;
  width?: number;
  footer?: React.ReactNode;
  mask?: boolean;
}

const GlobalDrawer = (props: Props) => {
  const { open, onClose, title, placement, children, width, footer, mask } = props;
  const screen = useResponsive();

  return (
    <StyledDrawer
      title={title}
      placement={placement || "right"}
      // width={width || 550}
      width={screen.xs ? '100%' : width || 550}
      onClose={(e: any) => onClose(e)}
      visible={open}
      footer={footer}
      mask={mask == false ? false : true}
    >
      {
        children || <p style={{ color: 'white' }}>Some contents...</p>
      }
    </StyledDrawer>
  );
};

const SubHeader = ({ children, style }: { children: any, style?: any }) => {
  return <SubHeaderWrapper style={style}>{children}</SubHeaderWrapper>
}

type SectionProps = {
  children: any,
  style?: any,
  dividerProps?: React.ComponentProps<typeof Divider>,
  title: string,
  titleStyle?: any,
}

interface BodySectionProps {
  title?: string;
  notCollapsible?: boolean;
  children: React.ReactNode;
}

const Section = (props: SectionProps) => {
  const { children, style, dividerProps, title } = props;
  const screen = useResponsive()

  const titleStyle = {
    width: '110px',
    fontSize: '14px',
    fontWeight: '700',
    textAlign: 'left',
    ...props.titleStyle
  }

  const contentWith = screen.xs ? '100%' : `calc(100% - 5px - ${titleStyle.width})`

  return (
    <div style={style}>
      <Divider orientation="left" orientationMargin="0" {...dividerProps}>
        <div style={titleStyle}>{title}</div>
      </Divider>
      <FlexWrapper flexEnd>
        <div style={{ width: contentWith }}>
          {children}
        </div>
      </FlexWrapper>
    </div>
  )
}

type BodyLayoutProps = {
  children: React.ReactNode;
};

export function ModalSection({ title, notCollapsible, children }: BodySectionProps) {
  return <>{children}</>; // Just a wrapper, logic handled in BodyLayout
}

export const BodyLayout = ({ children }: BodyLayoutProps) => {
  const items = useMemo(() => {
    return (
      React.Children.map(children, (child, index) => {
        if (React.isValidElement(child)) {
          const { title, notCollapsible, children } = child.props;
          const key = title || index.toString();
          return {
            key,
            title: title || '',
            notCollapsible,
            content: children,
          };
        } else {
          console.error('BodyLayout only accepts ModalSection as children');
          return null;
        }
      }) || []
    );
  }, [children]);

  const [activeKeys, setActiveKeys] = useState<string[]>(() => {
    return items.map((item) => `${item.key}`);
  });

  useEffect(() => {
    setActiveKeys(items.map((item) => `${item.key}`));
  }, [items]);

  return (
    <StyledCollapse>
      <Collapse
        activeKey={activeKeys}
        onChange={(keys) => {
          const noCollapsibleKeys = items
            .filter((item) => item.notCollapsible)
            .map((item) => item.key);
          setActiveKeys(Array.isArray(keys) ? [...keys, ...noCollapsibleKeys] : [keys]);
        }}
        expandIcon={({ isActive }) => (
          <span style={{ marginLeft: 'auto' }}>
            {isActive ? <UpOutlined /> : <DownOutlined />}
          </span>
        )}
      >
        {items.map((item) => (
          <Panel
            key={item.key}
            header={item.title}
            showArrow={!item.notCollapsible}
          >
            {item.content}
          </Panel>
        ))}
      </Collapse>
    </StyledCollapse>
  );
};

GlobalDrawer.Section = Section;
GlobalDrawer.SubHeader = SubHeader;
GlobalDrawer.ModalLayout = BodyLayout;
BodyLayout.ModalSection = ModalSection;

export default GlobalDrawer;


const StyledDrawer = styled(Drawer)`
  z-index: 10;

  .ant-drawer-content-wrapper {
    border-left: 1px solid rgba(222, 222, 222, 0.45);

    .ant-drawer-mask {
      background-color: #D9D9D9 !important;
      opacity: 0.4 !important;
    }
    .ant-drawer-content {
      border-radius: 4px !important;
      opacity: 1 !important;
    }
    .ant-drawer-header {
      box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25) !important;
      background-color: var(--modal-header-color) !important;
      border-radius: 4px 4px 0 0 !important;
      border-bottom: unset !important;
    }
    .ant-drawer-header-title {
      flex-direction: row-reverse;
    }
    .ant-drawer-close {
      font-size: 14px;
      margin-right: 0;
    }
    .ant-drawer-title {
      color: var(--white-color) !important;
      font-family: var(--font-normal) !important;
      font-size: 14px;
      text-align: center;
    }
    .ant-drawer-body {
      background-color: var(--main-background-color);  // body background color
      color: var(--white-text-color);
    }
    .ant-drawer-footer {
      border-top: unset !important;
      // text-align: center !important;
      padding: 16px 24px !important;
      box-shadow: 0px -4px 4px rgba(0, 0, 0, 0.25) !important;
      background-color: var(--modal-footer-color);
    }
    >div {
      .ant-btn {
        width: auto !important;
      }
    }
    .ant-btn {
      color: var(--white-color) !important;
      background-color: var(--primary-color);
      border-radius: 4px !important;
      border: 1px solid var(--primary-color);
      width: 10rem !important;
      &:hover{
        background-color: var(--primary-color);
        color: var(--white-color) !important;
        // border: unset !important;
      }
    }
    .ant-drawer-body {
      color: var(--white-color) !important;
    }
    .ant-drawer-close {
      svg {
        color: var(--white-color)
      }
    }

  }
  
`;

const SubHeaderWrapper = styled.div`
  background-color: var(--modal-subBody-color);
  padding: 20px 24px;
  margin: 0 -24px 10px -24px;
  color: var(--white-text-color);
`

const StyledCollapse = styled.div`
  margin-bottom: 20px;
  .ant-collapse {
    border: none !important;
  }
  .ant-collapse > .ant-collapse-item > .ant-collapse-header {
    background: #30324A !important;
    font-size: 14px !important;
  }
  .ant-collapse > .ant-collapse-item {
    border-bottom: none !important;
  }
  .ant-collapse-header-text {
      width: 100% !important;
      padding-top: 3px;
  }
  .ant-collapse-item .ant-collapse-content > .ant-collapse-content-box {
    background: #28293D !important;
    padding: 10px 1px !important;
    border: none !important;
  }
`;